import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ArrowLeft, Building2, Download, File, LucideAngularModule, Mail, Phone, ShieldUser, User } from 'lucide-angular';
import { Vendor, VendorDocument, VendorUserCreatePayload } from '../../../core/models/domain.models';
import { AuthService } from '../../../core/services/auth.service';
import { VendorsService } from '../services/vendors.service';
import { VendorDocumentsService } from '../../vendor-document/services/vendor-documents.service';
import { ToastService } from '../../../shared/ui/toast.service';

type VendorUserFormRow = {
  id: string;
  name: string;
  username: string;
  department: string;
  designation: string;
  email: string;
  contactNumber: string;
  isPrimary: boolean;
  status: string;
};

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './vendor-details.page.html',
  styleUrl: './vendor-details.page.scss',
})
export class VendorDetailsPage {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly vendorsService = inject(VendorsService);
  private readonly documentsService = inject(VendorDocumentsService);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(AuthService);

  protected readonly ArrowLeftIcon = ArrowLeft;
  protected readonly Building2Icon = Building2;
  protected readonly DownloadIcon = Download;
  protected readonly FileIcon = File;
  protected readonly UserIcon = User;
  protected readonly MailIcon = Mail;
  protected readonly PhoneIcon = Phone;
  protected readonly ShieldUserIcon = ShieldUser;

  protected readonly vendor = signal<Vendor | null>(null);
  protected readonly documents = signal<VendorDocument[]>([]);
  protected readonly users = signal<VendorUserFormRow[]>([]);
  protected readonly isVendorUser = computed(() => this.auth.user()?.role === 'vendor');
  protected readonly savingUser = signal(false);
  protected readonly editableUserId = computed(() => {
    const authUser = this.auth.user();
    const match = this.users().find(
      (user) =>
        user.id === (authUser?.id || '') ||
        user.username === (authUser?.username || '') ||
        user.email.toLowerCase() === (authUser?.email || '').toLowerCase(),
    );
    return match?.id || null;
  });
  protected readonly editableUser = computed(() => this.users().find((user) => user.id === this.editableUserId()) || null);
  protected readonly userStats = computed(() => ({
    total: this.users().length,
    primary: this.users().filter((user) => user.isPrimary).length,
  }));
  protected readonly profileHighlights = computed(() => {
    const vendor = this.vendor();
    return [
      { label: 'Vendor Type', value: vendor?.vendor_type || '-' },
      { label: 'State', value: vendor?.state || '-' },
      { label: 'Region', value: vendor?.region || '-' },
      { label: 'NOAR ID', value: vendor?.noar_id || '-' },
    ];
  });

  private vendorId = '';

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (!id) return;
      this.vendorId = id;
      if (this.isVendorUser() && id !== (this.auth.user()?.vendor_id || '')) {
        this.toast.show('You can only view your own vendor profile', '', 'danger');
        this.router.navigateByUrl(`/vendors/${this.auth.user()?.vendor_id || id}`);
        return;
      }
      this.loadAll();
    });
  }

  private loadAll(): void {
    this.vendorsService.get(this.vendorId).subscribe({
      next: (vendor) => {
        this.vendor.set(vendor);
      },
      error: () => this.vendor.set(null),
    });

    this.documentsService.listVendorDocuments(this.vendorId).subscribe((docs) => this.documents.set(docs));
    this.vendorsService.listUsers(this.vendorId).subscribe({
      next: (users) =>
        this.users.set(
          (users || []).map((user) => ({
            id: user.id,
            name: user.name,
            username: user.username,
            department: user.department,
            designation: user.designation,
            email: user.email,
            contactNumber: user.contact_number || '',
            isPrimary: !!user.is_primary,
            status: user.status || 'active',
          })),
        ),
      error: () => this.users.set([]),
    });
  }

  protected download(id: string): void {
    window.open(this.documentsService.downloadUrl(id), '_blank');
  }

  protected formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  protected profileRows(): Array<{ label: string; value: string }> {
    const vendor = this.vendor();
    if (!vendor) return [];

    return [
      { label: 'Vendor Name', value: vendor.vendor_name || '-' },
      { label: 'Vendor Type', value: vendor.vendor_type || '-' },
      { label: 'Email', value: vendor.email || '-' },
      { label: 'Contact Number', value: vendor.contact_number || '-' },
      { label: 'NOAR ID', value: vendor.noar_id || '-' },
      { label: 'GST Number', value: vendor.gst_number || '-' },
      { label: 'PAN Number', value: vendor.pan_number || '-' },
      { label: 'CIN', value: vendor.cin || '-' },
      { label: 'Exchange Portfolio ID', value: vendor.exchange_portfolio_id || '-' },
      { label: 'Generator Type', value: vendor.generator_type || '-' },
      { label: 'Voltage Level', value: vendor.voltage_level || '-' },
      { label: 'State', value: vendor.state || '-' },
      { label: 'Region', value: vendor.region || '-' },
      { label: 'DISCOM', value: vendor.discom || '-' },
      { label: 'Connected With CTU', value: vendor.connected_with_ctu ? 'Yes' : 'No' },
      { label: 'Exchange Registered', value: vendor.exchange_registered ? 'Yes' : 'No' },
      { label: 'Billing Address', value: vendor.billing_address || '-' },
      { label: 'Registered Address', value: vendor.registered_address || '-' },
      // { label: 'Status', value: vendor.status || '-' },
    ];
  }

  protected toggleOwnUserStatus(): void {
    const editableUserId = this.editableUserId();
    if (!editableUserId || !this.vendorId) {
      this.toast.show('Your user record could not be identified', '', 'danger');
      return;
    }
    const currentUser = this.editableUser();
    if (!currentUser) {
      this.toast.show('Your user record could not be identified', '', 'danger');
      return;
    }
    const nextStatus = this.normalizeUserStatus(currentUser.status) === 'active' ? 'deactive' : 'active';
    const confirmed = confirm(`Change your user status to ${nextStatus}?`);
    if (!confirmed) return;

    const updatedUsers = this.users().map((user) =>
      user.id === editableUserId
        ? {
            ...user,
            status: nextStatus,
          }
        : user,
    );

    this.savingUser.set(true);
    this.vendorsService
      .syncUsers(this.vendorId, this.toUserPayload(updatedUsers))
      .subscribe({
        next: () => {
          this.savingUser.set(false);
          this.users.set(updatedUsers);
          this.toast.show(`Your user status was updated to ${nextStatus}`, '', 'success');
        },
        error: () => {
          this.savingUser.set(false);
          this.toast.show('Failed to update user status', '', 'danger');
        },
      });
  }

  private normalizeUserStatus(status: string): string {
    return (status || '').trim().toLowerCase() === 'deactive' ? 'deactive' : 'active';
  }

  private toUserPayload(users: VendorUserFormRow[]): VendorUserCreatePayload[] {
    return users.map((user) => ({
      id: user.id,
      name: user.name,
      username: user.username,
      department: user.department,
      designation: user.designation,
      email: user.email,
      contact_number: user.contactNumber || null,
      password: null,
      is_primary: !!user.isPrimary,
      status: this.normalizeUserStatus(user.status),
    }));
  }
}
